import { useLazyQuery, type FetchPolicy } from '@apollo/client';
import type { ContentSearchInput, ContentSearchItem, ContentSearchPagination } from '@content-search/domain';
import { SEARCH_CONTENT } from '@content-search/graphql';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useMemo, useCallback } from 'react';

interface ContentSearchResponse {
  searchContent: {
    results: ContentSearchItem[];
    totalCount: number;
    executionTimeMs: number;
    pagination: ContentSearchPagination;
  };
}

export function useContentSearch() {
  const [executeSearch, { data, loading, error }] = useLazyQuery<ContentSearchResponse>(SEARCH_CONTENT, {
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true, // Show loading during refetches to indicate network activity
  });

  const errors: DomainError[] | null = useMemo(() => {
    if (!error) return null;

    track('SearchContentFailed', {
      errorMessage: error instanceof Error ? error.message : 'Unknown error',
    });

    return transformErrors(error);
  }, [error]);

  const searchResult = useMemo(() => {
    if (data?.searchContent) {
      track('SearchContentSucceeded', {
        totalCount: data.searchContent.totalCount.toString(),
        executionTimeMs: data.searchContent.executionTimeMs.toString(),
      });

      return data.searchContent;
    }

    return {
      results: [],
      totalCount: 0,
      executionTimeMs: 0,
      pagination: {
        skip: 0,
        batchSize: 10,
        hasMore: false,
        nextSkip: 0,
      },
    };
  }, [data]);

  // Create a search function that accepts new input and optional fetch policy, then executes the query
  const search = useCallback(
    (input: ContentSearchInput, fetchPolicy?: FetchPolicy) => {
      track('SearchContentInitiated');

      executeSearch({
        variables: { input },
        fetchPolicy: fetchPolicy || 'cache-and-network',
      });
    },
    [executeSearch]
  );

  return {
    searchResult,
    isSearching: loading,
    errors,
    search,
  };
}
